import { Activity, Building2, FileCheck, Mail, MapPin, Network, ShieldAlert, Users } from 'lucide-react';

/*
 * Content for the Healthcare scenario walkthrough, drawn from the `healthcare`
 * industry demo pack in the BOSSS repo.
 *
 * Kestrel Health System is a composite demo organization, not a real customer.
 * Artifact strings are verbatim record titles from the pack. Note that this
 * pack's ORG.md storyline contract has drifted from what was actually authored
 * in the YAML (for example the flagship alert is `al-ransomware-mass-encryption`,
 * not ORG.md's `al-ransomware-clinical`), so titles here were read from the
 * domain YAML directly rather than from ORG.md.
 *
 * The framing rules this content must satisfy are enforced by kestrel.test.js.
 */

export const kestrelScenario = {
  org: {
    name: 'Kestrel Health System',
    subtitle: 'Five situations a health system actually faces, from the bedside to the board report.',
    framingNote: 'a composite organization in the BOSSS demo environment',
    profile: [
      { icon: Users, text: '~4,200 employees' },
      { icon: Building2, text: '3 acute-care hospitals, ~600 beds' },
      { icon: MapPin, text: '~22 outpatient clinics' },
      { icon: Network, text: 'Kestrel Virtual Care subsidiary' },
    ],
  },
  scenarios: [
    {
      id: 'clinical-ransomware',
      number: 1,
      title: 'Ransomware Halts Clinical Care',
      tagline: 'The EHR goes dark and the emergency department reverts to paper',
      icon: ShieldAlert,
      frameworks: ['HIPAA Security Rule', 'NIST CSF 2.0'],
      situation: {
        narrative: 'A phishing email reaches a clinical workstation at an emergency-department nursing station. Two '
          + 'days later the attacker moves laterally and detonates ransomware, encrypting the CoreChart EHR '
          + 'production cluster and the PACS imaging file share. All three hospitals lose EHR access and the '
          + 'emergency department declares downtime, reverting to paper procedures.',
        cast: [
          { name: 'Sandra Reinholt', role: 'Chief Information Security Officer' },
          { name: 'William Hartley', role: 'IT Director' },
          { name: 'Jordan Whitfield', role: 'SOC Manager' },
          { name: 'Dr. Alan Feinberg', role: 'Chief Medical Information Officer' },
        ],
      },
      timing: [
        { at: '−18 days', label: 'Phishing email lands' },
        { at: '−16 days', label: 'Workstation compromise, lateral movement' },
        { at: '−15 days', label: 'Ransomware detonation — ED downtime declared' },
        { at: '−2 days', label: 'Restore complete, board reported' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Mass-encryption behaviour is detected on the clinical workstation',
          artifact: 'Ransomware mass-encryption behavior on clinical workstation',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Escalated to a full investigation case',
          artifact: 'Ransomware Clinical-Downtime Investigation',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Clinical outage ticket opens and downtime is formally declared',
          artifact: 'Ransomware detection on clinical workstation — EHR downtime declared',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record covering the encrypted clinical estate',
          artifact: 'Ransomware Encryption of the EHR Production Cluster',
        },
        {
          module: 'BCRP',
          href: '/platform/resilience',
          action: 'Continuity and recovery plans drive the restore from the DR data center',
          artifact: 'EHR & Clinical Operations Ransomware Continuity Plan',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Backup and segmentation weaknesses become a tracked POA&M',
          artifact: 'POA&M — Ransomware Backup/Segmentation Hardening',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Clinical downtime procedure and board reporting close the loop',
          artifact: 'Q3 Board Security Report: Ransomware Incident',
        },
      ],
      enables: [
        'The CISO, the IT director and the chief medical information officer track one incident — clinical impact and security response are the same record, not two meetings.',
        'The downtime procedure the emergency department follows is a controlled document linked to the incident that invoked it.',
      ],
      impact: {
        entities: [
          { name: 'Kestrel Health System', detail: '3 hospitals affected', location: 'Merriston, OH' },
        ],
        locations: [
          { name: 'Kestrel Central Hospital Campus', detail: 'Headquarters · flagship trauma center', location: 'Merriston, OH' },
          { name: 'Kestrel West Community Hospital', detail: 'Office', location: 'Westbrook, OH' },
          { name: 'Kestrel Riverside Hospital', detail: 'Office', location: 'Riverton, OH' },
          { name: 'Kestrel DR / Backup Data Center', detail: 'Disaster Recovery Site · restore source', location: 'Fairhaven, OH' },
        ],
        applications: [
          { name: 'CoreChart EHR Production Cluster', detail: 'Encrypted', location: 'Merriston, OH' },
          { name: 'PACS Imaging File Share', detail: 'Encrypted', location: 'Merriston, OH' },
          { name: 'Clinical Workstation — ED Nursing Station', detail: 'Initial compromise', location: 'Merriston, OH' },
          { name: 'Nightly EHR Backup', detail: 'Restore source', location: 'Fairhaven, OH' },
        ],
        vendors: [
          { name: 'Cornerstone Clinical Systems', detail: 'EHR platform — Business Associate', location: 'Madison, WI' },
          { name: 'Harbor Imaging Systems', detail: 'PACS — Business Associate', location: 'Boston, MA' },
          { name: 'Aegis Endpoint Security', detail: 'Endpoint detection', location: 'Austin, TX' },
          { name: 'Lumen Security Analytics', detail: 'SIEM', location: 'Reston, VA' },
        ],
        data: [
          { name: 'EHR Clinical Record', detail: 'Data set — ePHI', location: 'Merriston, OH' },
          { name: 'Clinical Encounter Note', detail: '~14M encounter notes', location: 'Merriston, OH' },
          { name: 'Diagnostic Imaging Study (DICOM)', detail: '~2.4M imaging studies', location: 'Merriston, OH' },
          { name: 'Medical Record Number (MRN)', detail: '~1.8M patient records', location: 'Merriston, OH' },
        ],
      },
    },
    {
      id: 'phi-breach',
      number: 2,
      title: 'PHI Breach and OCR Notification',
      tagline: 'A compromised mailbox, 500+ records, and a federal notification clock',
      icon: Mail,
      frameworks: ['HIPAA Breach Notification Rule', 'HITECH', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Phishing compromises the mailbox of a release-of-information specialist in Health Information '
          + 'Management. A suspicious auto-forwarding rule is created, and protected health information for more '
          + 'than 500 individuals is exfiltrated — crossing the threshold that triggers HHS Office for Civil '
          + 'Rights notification. The enabling control gap is incomplete MFA on workforce email.',
        cast: [
          { name: 'Teresa Vandenberg', role: 'Chief Privacy & Compliance Officer' },
          { name: 'Robert Ashby', role: 'Privacy Officer' },
          { name: 'Karen Lindqvist', role: 'Director, Health Information Management' },
          { name: 'Gregory Halloran', role: 'General Counsel' },
        ],
      },
      timing: [
        { at: '−40 days', label: 'Phishing, mailbox compromise' },
        { at: '−35 days', label: 'PHI exfiltration discovered' },
        { at: '−20 days', label: 'OCR notification prepared' },
        { at: '−5 days', label: 'Notification filed' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'The auto-forwarding rule is flagged as an exfiltration indicator',
          artifact: 'Suspicious mailbox auto-forwarding rule created',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Escalated to a breach investigation case',
          artifact: 'ePHI Breach — Mailbox Compromise Investigation',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record establishes the scope of the exposure',
          artifact: 'PHI Exfiltration via Compromised HIM Mailbox',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The breach is recorded as a finding, with the record count that drives the obligation',
          artifact: 'PHI Exfiltration via HIM Mailbox Compromise (500+ Records)',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The enabling control gap is tracked separately from the breach itself',
          artifact: 'Incomplete MFA on Workforce Email Accounts',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'The federal notification is produced against the breach-notification policy',
          artifact: 'HHS OCR Breach Notification (500+ Individuals)',
        },
      ],
      enables: [
        'The record count that triggers the federal threshold lives on the finding, so the notification obligation is derived from evidence rather than reconstructed by hand.',
        'The breach and the MFA gap that enabled it are separate records with separate owners — closing one does not silently close the other.',
      ],
      impact: {
        entities: [
          { name: 'Kestrel Health System', detail: 'Health Information Management', location: 'Merriston, OH' },
        ],
        locations: [
          { name: 'Kestrel Central Hospital Campus', detail: 'Headquarters · HIM and Privacy Office', location: 'Merriston, OH' },
        ],
        applications: [
          { name: 'Microsoft 365 mailbox', detail: 'Compromised — HIM staff', location: 'Merriston, OH' },
          { name: 'Keystone Identity SSO / IdP', detail: 'MFA coverage gap', location: 'Merriston, OH' },
        ],
        vendors: [
          { name: 'Keystone Identity', detail: 'SSO / identity provider', location: 'San Francisco, CA' },
          { name: 'Lumen Security Analytics', detail: 'SIEM', location: 'Reston, VA' },
        ],
        data: [
          { name: 'Patient Social Security Number', detail: '~1.8M patient records', location: 'Merriston, OH' },
          { name: 'Medical Record Number (MRN)', detail: '~1.8M patient records', location: 'Merriston, OH' },
          { name: 'Clinical Encounter Note', detail: '~14M encounter notes', location: 'Merriston, OH' },
          { name: 'EHR Clinical Record', detail: 'Data set — ePHI', location: 'Merriston, OH' },
        ],
      },
    },
    {
      id: 'business-associate-breach',
      number: 3,
      title: 'Business Associate Breach',
      tagline: 'The billing vendor is breached, and the BAA obligations activate',
      icon: Building2,
      frameworks: ['HIPAA', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Meridian Revenue Partners, the revenue-cycle-management outsourcer and a contracted Business '
          + 'Associate, discloses a breach of its environment exposing Kestrel patient data. The Business '
          + 'Associate Agreement obligations activate: due-diligence re-assessment, encryption-at-rest review, '
          + 'and downstream notification. A routine annual review of the cloud host runs alongside it.',
        cast: [
          { name: 'Gregory Simms', role: 'Third-Party / Vendor Risk Analyst' },
          { name: 'Robert Ashby', role: 'Privacy Officer' },
          { name: 'Gregory Halloran', role: 'General Counsel' },
        ],
      },
      timing: [
        { at: '−30 days', label: 'Vendor breach notification received' },
        { at: '−26 days', label: 'Due-diligence re-assessment opened' },
        { at: 'Ongoing', label: 'Encryption gap tracked as accepted risk' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'The disclosure is worked as a case, not an inbox thread',
          artifact: 'Business-Associate Breach — Meridian RCM',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record for the third-party exposure',
          artifact: 'Business Associate Breach — Meridian Revenue Partners',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'An out-of-cycle due-diligence package opens against the vendor',
          artifact: 'Meridian RCM — Breach Re-Assessment',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The security questionnaire is re-issued; encryption at rest comes back short',
          artifact: 'Meridian RCM — Post-Breach Security Re-Assessment',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The gap becomes a logged, time-bound accepted risk rather than a silent exposure',
          artifact: 'Accepted Risk — Meridian RCM ePHI Encryption-at-Rest Gap Pending Corrective Action',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Due-diligence findings issue as a report',
          artifact: 'Meridian Revenue Partners Due-Diligence Re-Assessment Report',
        },
      ],
      enables: [
        'The Business Associate Agreement, the assessment and the accepted risk sit together, so an obligation is checkable at the moment it is tested.',
        'Two vendor reviews run in parallel — one breach-driven, one routine — under the same due-diligence process rather than two ad-hoc efforts.',
      ],
      impact: {
        entities: [
          { name: 'Kestrel Health System', detail: 'Patient Financial Services', location: 'Merriston, OH' },
          { name: 'Meridian Revenue Partners', detail: 'Business Associate — breached', location: 'Nashville, TN' },
        ],
        locations: [
          { name: 'Kestrel Medical Group Administrative Offices', detail: 'Office · revenue cycle', location: 'Merriston, OH' },
          { name: 'Meridian Revenue Partners Headquarters', detail: 'Vendor site — breached', location: 'Nashville, TN' },
        ],
        applications: [
          { name: 'Meridian RCM platform', detail: 'Billing and revenue cycle', location: 'Nashville, TN' },
          { name: 'CoreChart EHR Production Cluster', detail: 'Billing extract source', location: 'Merriston, OH' },
        ],
        vendors: [
          { name: 'Meridian Revenue Partners', detail: 'Breached — RCM outsourcer', location: 'Nashville, TN' },
          { name: 'Summit Cloud Services', detail: 'Routine annual review', location: 'Denver, CO' },
        ],
        data: [
          { name: 'Billing & Claim Record', detail: 'Data set', location: 'Nashville, TN' },
          { name: 'Insurance Claim (837)', detail: '~4.2M claims/year', location: 'Nashville, TN' },
          { name: 'Medical Record Number (MRN)', detail: '~1.8M patient records', location: 'Merriston, OH' },
          { name: 'EHR to Meridian billing extract', detail: 'Transmission', location: 'Merriston, OH → Nashville, TN' },
        ],
      },
    },
    {
      id: 'iomt-vulnerability',
      number: 4,
      title: 'Medical Device Vulnerability',
      tagline: 'An infusion-pump CVE on a flat biomedical VLAN',
      icon: Activity,
      frameworks: ['HIPAA Security Rule', 'NIST CSF 2.0', 'FDA CFR Part 11'],
      situation: {
        narrative: 'A published CVE allows unauthenticated firmware manipulation on the Vantage infusion-pump fleet '
          + 'deployed across all three hospitals. Compounding it, the biomedical-device VLAN is flat, permitting '
          + 'lateral movement from the general clinical network. Clinical Engineering and Security run a joint '
          + 'remediation targeting a future maintenance window — patient-connected devices cannot simply be '
          + 'rebooted.',
        cast: [
          { name: 'Victor Nagy', role: 'Director, Clinical / Biomedical Engineering' },
          { name: 'Marcus Feldstein', role: 'Medical Device Security Analyst' },
          { name: 'Amanda Sørenson', role: 'Biomedical Engineer — infusion devices' },
          { name: 'Douglas Kim', role: 'Network Engineer' },
        ],
      },
      timing: [
        { at: '−50 days', label: 'CVE published and ingested' },
        { at: 'Last quarter', label: 'Segmentation gap assessed progressively' },
        { at: '+30 days', label: 'Remediation targets a maintenance window' },
      ],
      response: [
        {
          module: 'Pentest',
          href: '/platform/pentest',
          action: 'The device exposure is confirmed by testing, not assumed from the advisory',
          artifact: 'Unauthenticated Firmware Update on Vantage Infusion-Pump Fleet (Storyline 4 CVE)',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Device security is worked as a tracked investigation',
          artifact: 'Medical Device (IoMT) Security Investigation — Infusion-Pump CVE',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The network weakness is recorded separately from the firmware defect',
          artifact: 'Flat Biomedical-Device VLAN Permits Lateral Movement',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Clinical Engineering picks up the fleet remediation ticket',
          artifact: 'Unauthenticated firmware manipulation risk on Vantage infusion-pump fleet',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Segmentation is scheduled as an approved change with clinical sign-off',
          artifact: 'Segment the biomedical-device VLAN from the general clinical network',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'The controlling procedure governs how device security is handled',
          artifact: 'Medical Device (IoMT) Security Procedure',
        },
      ],
      enables: [
        'A device defect and a network weakness are separate findings with separate owners, because Clinical Engineering fixes one and Networking fixes the other.',
        'The segmentation change carries clinical approval before it touches a VLAN that patient-connected pumps depend on.',
      ],
      impact: {
        entities: [
          { name: 'Kestrel Health System', detail: 'Clinical / Biomedical Engineering', location: 'Merriston, OH' },
        ],
        locations: [
          { name: 'Kestrel Central Hospital Campus', detail: 'Headquarters · biomedical engineering shop', location: 'Merriston, OH' },
          { name: 'Kestrel West Community Hospital', detail: 'Office · device fleet', location: 'Westbrook, OH' },
          { name: 'Kestrel Riverside Hospital', detail: 'Office · device fleet', location: 'Riverton, OH' },
          { name: 'Eastside Outpatient Clinic', detail: 'Branch Office · ambulatory devices', location: 'Merriston, OH' },
        ],
        applications: [
          { name: 'Vantage Infusion Pump Fleet', detail: 'Unpatched CVE', location: 'Merriston, OH' },
          { name: 'Vantage Patient Monitor Fleet', detail: 'Same VLAN', location: 'Merriston, OH' },
          { name: 'Biomedical-device VLAN', detail: 'Segmentation gap', location: 'Merriston, OH' },
        ],
        vendors: [
          { name: 'Vantage Medical Devices', detail: 'Device OEM', location: 'Minneapolis, MN' },
          { name: 'SentryMed Managed Detection', detail: 'MDR augmentation', location: 'Reston, VA' },
        ],
        data: [
          { name: 'Medication Order', detail: 'Infusion pump programming', location: 'Merriston, OH' },
          { name: 'EHR Clinical Record', detail: 'Data set — ePHI', location: 'Merriston, OH' },
        ],
      },
    },
    {
      id: 'hipaa-risk-analysis',
      number: 5,
      title: 'HIPAA Security Rule Risk Analysis',
      tagline: 'The annual §164.308(a)(1) analysis, and the POA&M it produces',
      icon: FileCheck,
      frameworks: ['HIPAA Security Rule', 'NIST CSF 2.0', 'SOC 2'],
      situation: {
        narrative: 'The annual HIPAA Security Rule §164.308(a)(1) risk analysis runs across the ePHI estate: '
          + 'identified control gaps, a plan of action and milestones, and board reporting. Internal Audit '
          + 'reviews, and the workforce access-review campaign stands as the adjacent evidence artifact.',
        cast: [
          { name: 'Sophia Delgado', role: 'GRC Analyst' },
          { name: 'Teresa Vandenberg', role: 'Chief Privacy & Compliance Officer' },
          { name: 'Charles Dembski', role: 'Internal Audit Director' },
        ],
      },
      timing: [
        { at: '−90 days', label: 'Assessment kickoff' },
        { at: '−10 days', label: 'Report finalized' },
        { at: 'Future dated', label: 'POA&M actions tracked to due dates' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The program policy sits under governance with attestation history',
          artifact: 'HIPAA Security Rule Program Policy',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A formal assessment runs against the authority itself',
          artifact: 'HIPAA Security Rule §164.308(a)(1) Risk Analysis (2026)',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Unremediated ePHI control gaps are recorded as a finding',
          artifact: 'HIPAA §164.308(a)(1) Risk Analysis Identified Unremediated ePHI Control Gaps',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Gaps become a plan of action and milestones with owners and dates',
          artifact: 'POA&M — HIPAA §164.308(a)(1) Risk Analysis Control Gaps',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A certification project tracks the assessment through to board reporting',
          artifact: 'HIPAA Security Rule Program Assessment — Kestrel Health System',
        },
      ],
      enables: [
        'The risk analysis, the gaps it found and the plan that closes them are one chain — an auditor follows it instead of reconciling three documents.',
        'Access-review evidence is a live campaign record rather than a screenshot pasted into an appendix.',
      ],
      impact: {
        entities: [
          { name: 'Kestrel Health System', detail: 'Whole-system scope', location: 'Merriston, OH' },
          { name: 'Kestrel Virtual Care', detail: 'Subsidiary — SOC 2 scope' },
        ],
        locations: [
          { name: 'Kestrel Central Hospital Campus', detail: 'Headquarters', location: 'Merriston, OH' },
          { name: 'Kestrel Virtual Care Operations Hub', detail: 'Office · telehealth operations', location: 'Merriston, OH' },
          { name: 'Kestrel DR / Backup Data Center', detail: 'Disaster Recovery Site', location: 'Fairhaven, OH' },
        ],
        applications: [
          { name: 'CoreChart EHR Production Cluster', detail: 'System of record', location: 'Merriston, OH' },
          { name: 'Keystone Identity SSO / IdP', detail: 'Access recertification', location: 'Merriston, OH' },
          { name: 'PACS Imaging File Share', location: 'Merriston, OH' },
        ],
        vendors: [
          { name: 'Cobalt Assurance Group', detail: 'Independent assessor', location: 'Chicago, IL' },
          { name: 'Summit Cloud Services', detail: 'HITRUST-hosted clinical workloads', location: 'Denver, CO' },
        ],
        data: [
          { name: 'EHR Clinical Record', detail: 'Data set — ePHI', location: 'Merriston, OH' },
          { name: 'Medical Record Number (MRN)', detail: '~1.8M patient records', location: 'Merriston, OH' },
          { name: 'Patient Social Security Number', detail: '~1.8M patient records', location: 'Merriston, OH' },
        ],
      },
    },
  ],
};
